<script setup>
// Boletos do cliente e as ações de cobrança. Porta da AbaFinanceiro do eryx-sgp.
// As regras (vencido, valor corrigido, quem pode ser cobrado) vêm prontas do
// servidor; aqui só se mostra e se aciona.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import messages from './i18n';
import { erroDaApi } from './api';
import { data as formataData, dinheiro, hojeMais } from './format';
import SgpSelo from './SgpSelo.vue';

const props = defineProps({
  api: { type: Object, required: true },
  aceitaBotao: { type: Boolean, default: false },
});

const { t } = useI18n({ messages });

const dados = ref(null);
const carregando = ref(false);
const erro = ref('');
const filtro = ref('cobrar');
const enviando = ref('');
const feito = ref('');
// Boletos cujo PDF o SGP não serve: aí o que dá para mandar é o link.
const semPdf = ref(new Set());

const promessaAberta = ref(false);
const dataPromessa = ref('');
const lancando = ref(false);
const erroPromessa = ref('');
const promessa = ref(null);

const visiveis = computed(() =>
  filtro.value === 'cobrar'
    ? dados.value.titulos.filter(b => b.pode_cobrar)
    : dados.value.titulos
);

const busca = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const resposta = await props.api.titulos();
    dados.value = resposta.data;
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  } finally {
    carregando.value = false;
  }
};

const marca = chave => {
  feito.value = chave;
  setTimeout(() => {
    if (feito.value === chave) feito.value = '';
  }, 2000);
};

// Uma chave por clique: o servidor usa para que um clique duplo não cobre o
// cliente duas vezes.
// A chave do pix não pode ser `pix-<id>`: essa já marca o "copiar Pix".
const envia = async (boleto, formato, chave = `${formato}-${boleto.id}`) => {
  enviando.value = chave;
  erro.value = '';
  try {
    await props.api.enviaTitulo(boleto.id, formato, crypto.randomUUID());
    marca(chave);
  } catch (error) {
    if (formato === 'pdf' && error?.response?.status === 502) {
      semPdf.value = new Set([...semPdf.value, boleto.id]);
    }
    erro.value = erroDaApi(error, t('NEUXS_SGP.FINANCEIRO.ERRO_ENVIO'));
  } finally {
    enviando.value = '';
  }
};

const enviaLink = async boleto => {
  const chave = `link-${boleto.id}`;
  enviando.value = chave;
  erro.value = '';
  try {
    await props.api.enviaTexto(
      t('NEUXS_SGP.FINANCEIRO.TEXTO_LINK', {
        data: formataData(boleto.vencimento),
        valor: dinheiro(boleto.valor_atual),
        link: boleto.link_cobranca || boleto.link,
      })
    );
    marca(chave);
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.FINANCEIRO.ERRO_ENVIO'));
  } finally {
    enviando.value = '';
  }
};

const copia = async (texto, chave) => {
  await navigator.clipboard.writeText(texto);
  marca(chave);
};

// Só `liberado` é aprovação: um "respondeu" lido como "aprovou" faria o
// operador prometer uma religação que não vai acontecer.
const lancaPromessa = async () => {
  lancando.value = true;
  erroPromessa.value = '';
  promessa.value = null;
  try {
    const resposta = await props.api.promessa(dataPromessa.value);
    promessa.value = resposta.data;
    if (resposta.data.liberado) busca();
  } catch (error) {
    erroPromessa.value = erroDaApi(
      error,
      t('NEUXS_SGP.FINANCEIRO.PROMESSA.ERRO')
    );
  } finally {
    lancando.value = false;
  }
};

onMounted(busca);
</script>

<template>
  <div
    v-if="carregando && !dados"
    class="flex items-center gap-2 text-n-slate-11"
  >
    <Spinner :size="14" />
    {{ t('NEUXS_SGP.CONSULTANDO') }}
  </div>

  <div v-else-if="!dados" class="flex flex-col items-start gap-2">
    <p class="text-n-ruby-11">{{ erro }}</p>
    <Button
      :label="t('NEUXS_SGP.TENTAR_DE_NOVO')"
      size="xs"
      variant="ghost"
      icon="i-lucide-refresh-cw"
      @click="busca"
    />
  </div>

  <div v-else class="flex flex-col gap-3">
    <!-- Em três segundos o operador sabe se há pendência. -->
    <div class="grid grid-cols-3 gap-2 text-center">
      <div
        class="px-1 py-2 rounded-lg bg-n-alpha-2"
        :class="{ 'text-n-ruby-11': dados.resumo.vencidos > 0 }"
      >
        <div class="font-semibold">{{ dados.resumo.vencidos }}</div>
        <div class="text-xs">{{ t('NEUXS_SGP.FINANCEIRO.VENCIDOS') }}</div>
      </div>
      <div class="px-1 py-2 rounded-lg bg-n-alpha-2">
        <div class="font-semibold">{{ dados.resumo.em_aberto }}</div>
        <div class="text-xs">{{ t('NEUXS_SGP.FINANCEIRO.A_VENCER') }}</div>
      </div>
      <div class="px-1 py-2 rounded-lg bg-n-alpha-2">
        <div class="font-semibold truncate">
          {{ dinheiro(dados.resumo.total_a_cobrar) }}
        </div>
        <div class="text-xs">{{ t('NEUXS_SGP.FINANCEIRO.A_COBRAR') }}</div>
      </div>
    </div>

    <!-- Sem isto, cliente com dois contratos veria os boletos misturados. -->
    <p class="text-xs text-n-slate-11">
      {{
        dados.por_contrato
          ? t('NEUXS_SGP.FINANCEIRO.POR_CONTRATO', { contrato: dados.contrato })
          : t('NEUXS_SGP.FINANCEIRO.POR_DOCUMENTO')
      }}
    </p>

    <div class="flex items-center gap-1">
      <Button
        :label="t('NEUXS_SGP.FINANCEIRO.EM_ABERTO')"
        size="xs"
        :variant="filtro === 'cobrar' ? 'faded' : 'ghost'"
        color="slate"
        @click="filtro = 'cobrar'"
      />
      <Button
        :label="t('NEUXS_SGP.FINANCEIRO.TODOS')"
        size="xs"
        :variant="filtro === 'todos' ? 'faded' : 'ghost'"
        color="slate"
        @click="filtro = 'todos'"
      />
      <Button
        class="ml-auto"
        icon="i-lucide-refresh-cw"
        size="xs"
        variant="ghost"
        color="slate"
        :is-loading="carregando"
        :title="t('NEUXS_SGP.ATUALIZAR')"
        @click="busca"
      />
    </div>

    <p v-if="erro" class="text-n-ruby-11">{{ erro }}</p>

    <div class="rounded-lg outline outline-1 outline-n-weak">
      <button
        type="button"
        class="flex items-center w-full gap-2 px-2 py-1.5 text-xs font-medium text-n-slate-12"
        @click="promessaAberta = !promessaAberta"
      >
        <span class="i-lucide-calendar-clock" />
        {{ t('NEUXS_SGP.FINANCEIRO.PROMESSA.TITULO') }}
        <span
          class="ml-auto i-lucide-chevron-down"
          :class="{ 'rotate-180': promessaAberta }"
        />
      </button>
      <div v-if="promessaAberta" class="flex flex-col gap-2 px-2 pb-2">
        <label class="flex items-center gap-2 text-xs">
          {{ t('NEUXS_SGP.FINANCEIRO.PROMESSA.PAGAR_ATE') }}
          <!-- O seletor já nasce dentro do que a regra aceita (até 90 dias). -->
          <input
            v-model="dataPromessa"
            type="date"
            :min="hojeMais(0)"
            :max="hojeMais(90)"
            :disabled="lancando"
            class="flex-1 px-2 py-1 mb-0 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </label>
        <Button
          :label="t('NEUXS_SGP.FINANCEIRO.PROMESSA.LANCAR')"
          size="xs"
          :disabled="!dataPromessa"
          :is-loading="lancando"
          @click="lancaPromessa"
        />
        <p v-if="erroPromessa" class="text-n-ruby-11">{{ erroPromessa }}</p>
        <div
          v-if="promessa"
          class="flex flex-col gap-1 p-2 text-xs rounded-lg"
          :class="
            promessa.liberado
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          <strong>
            {{
              promessa.liberado
                ? t('NEUXS_SGP.FINANCEIRO.PROMESSA.LIBERADO')
                : t('NEUXS_SGP.FINANCEIRO.PROMESSA.NAO_LIBERADO')
            }}
          </strong>
          <span v-if="promessa.liberado">
            {{
              t('NEUXS_SGP.FINANCEIRO.PROMESSA.RESUMO', {
                data: formataData(promessa.data),
                dias: promessa.dias,
                protocolo: promessa.protocolo || '—',
              })
            }}
          </span>
          <span v-if="promessa.msg">{{ promessa.msg }}</span>
        </div>
      </div>
    </div>

    <p v-if="visiveis.length === 0" class="text-n-slate-11">
      {{
        filtro === 'cobrar'
          ? t('NEUXS_SGP.FINANCEIRO.NENHUM_ABERTO')
          : t('NEUXS_SGP.FINANCEIRO.NENHUM')
      }}
    </p>

    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="boleto in visiveis"
        :key="boleto.id"
        class="flex flex-col gap-1 p-2 rounded-lg outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-between gap-2">
          <SgpSelo
            :tom="boleto.status"
            :texto="t(`NEUXS_SGP.FINANCEIRO.STATUS.${boleto.status}`)"
          />
          <span class="font-semibold">{{ dinheiro(boleto.valor_atual) }}</span>
        </div>
        <div class="flex flex-wrap gap-x-2 text-xs text-n-slate-11">
          <span>
            {{
              t('NEUXS_SGP.FINANCEIRO.VENCIMENTO', {
                data: formataData(boleto.vencimento),
              })
            }}
          </span>
          <span v-if="boleto.dias_em_atraso > 0" class="text-n-ruby-11">
            {{
              t('NEUXS_SGP.FINANCEIRO.ATRASO', {
                dias: boleto.dias_em_atraso,
              })
            }}
          </span>
          <span v-if="boleto.status === 'pago' && boleto.pagamento">
            {{
              t('NEUXS_SGP.FINANCEIRO.PAGO_EM', {
                data: formataData(boleto.pagamento),
              })
            }}
          </span>
        </div>
        <p
          v-if="boleto.valor_atual > boleto.valor"
          class="text-xs text-n-slate-10"
        >
          {{
            t('NEUXS_SGP.FINANCEIRO.ORIGINAL', {
              valor: dinheiro(boleto.valor),
            })
          }}
        </p>
        <p v-if="boleto.descricao" class="text-xs truncate text-n-slate-11">
          {{ boleto.descricao }}
        </p>

        <div v-if="boleto.pode_cobrar" class="flex flex-wrap gap-1 pt-1">
          <Button
            v-if="aceitaBotao && (boleto.link_cobranca || boleto.link)"
            :label="t('NEUXS_SGP.FINANCEIRO.BOTAO')"
            :icon="
              feito === `botao-${boleto.id}`
                ? 'i-lucide-check'
                : 'i-lucide-send'
            "
            size="xs"
            :is-loading="enviando === `botao-${boleto.id}`"
            @click="envia(boleto, 'botao')"
          />
          <Button
            v-if="aceitaBotao && boleto.link_pix"
            :label="t('NEUXS_SGP.FINANCEIRO.PIX')"
            :icon="
              feito === `pix-botao-${boleto.id}`
                ? 'i-lucide-check'
                : 'i-lucide-qr-code'
            "
            size="xs"
            :is-loading="enviando === `pix-botao-${boleto.id}`"
            @click="envia(boleto, 'pix', `pix-botao-${boleto.id}`)"
          />
          <Button
            v-if="boleto.link"
            :label="t('NEUXS_SGP.FINANCEIRO.PDF')"
            :icon="
              feito === `pdf-${boleto.id}`
                ? 'i-lucide-check'
                : 'i-lucide-file-text'
            "
            size="xs"
            variant="faded"
            :is-loading="enviando === `pdf-${boleto.id}`"
            @click="envia(boleto, 'pdf')"
          />
          <Button
            v-if="semPdf.has(boleto.id)"
            :label="t('NEUXS_SGP.FINANCEIRO.LINK')"
            :icon="
              feito === `link-${boleto.id}` ? 'i-lucide-check' : 'i-lucide-link'
            "
            size="xs"
            variant="faded"
            :is-loading="enviando === `link-${boleto.id}`"
            @click="enviaLink(boleto)"
          />
          <Button
            v-if="boleto.linha_digitavel"
            :icon="
              feito === `linha-${boleto.id}`
                ? 'i-lucide-check'
                : 'i-lucide-copy'
            "
            size="xs"
            variant="ghost"
            color="slate"
            :title="t('NEUXS_SGP.FINANCEIRO.COPIAR_LINHA')"
            @click="copia(boleto.linha_digitavel, `linha-${boleto.id}`)"
          />
          <Button
            v-if="boleto.tem_pix"
            :icon="
              feito === `pix-${boleto.id}`
                ? 'i-lucide-check'
                : 'i-lucide-qr-code'
            "
            size="xs"
            variant="ghost"
            color="slate"
            :title="t('NEUXS_SGP.FINANCEIRO.COPIAR_PIX')"
            @click="copia(boleto.codigo_pix, `pix-${boleto.id}`)"
          />
          <a
            v-if="boleto.link"
            :href="boleto.link"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center px-2 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            :title="t('NEUXS_SGP.FINANCEIRO.ABRIR')"
          >
            <span class="i-lucide-external-link" />
          </a>
        </div>
      </li>
    </ul>

    <!-- O SGP devolve no máximo 250: dizer que há mais, e não fingir que é tudo. -->
    <p v-if="dados.total > dados.parcial" class="text-xs text-n-slate-10">
      {{
        t('NEUXS_SGP.FINANCEIRO.MOSTRANDO', {
          parcial: dados.parcial,
          total: dados.total,
        })
      }}
    </p>
  </div>
</template>
